// QuickBooks Status — Wave D.
//
// Mirrors `tricivo_system/operations/quickbooks_settings.php`: connection
// status card, sync stats, Connect / Disconnect / Sync Now buttons. Admin-
// gated by router.

import { useNavigate } from 'react-router-dom';
import { Building2, CheckCircle, Link2, RefreshCw, Settings, Unlink, Users, XCircle } from 'lucide-react';
import { PageHeader } from '../../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { Badge } from '../../../../components/ui/Badge/Badge';
import { ConfirmModal } from '../../../../components/ui/ConfirmModal/ConfirmModal';
import { useState } from 'react';
import {
  useQbDisconnect,
  useQbInitiateOauth,
  useQbStatus,
} from './hooks/useQuickBooks';
import styles from './QuickBooksStatusPage.module.css';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function QuickBooksStatusPage(): JSX.Element {
  const navigate = useNavigate();
  // Connection status is module-agnostic; the status page is the shared
  // OAuth surface, so it queries the default (collections) module.
  const status = useQbStatus('collections');
  const initiate = useQbInitiateOauth();
  const disconnect = useQbDisconnect();
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  if (status.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="QuickBooks Integration" />
        <div className={styles.loading}>
          <Spinner />
        </div>
      </div>
    );
  }

  const data = status.data ?? {
    connected: false,
    realm_id: null,
    connected_at: null,
    connected_by: null,
    last_sync_at: null,
    last_sync_status: null,
    token_expires_at: null,
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="QuickBooks Integration"
        subtitle="Manage connection and sync transactions to QuickBooks"
        actions={
          data.connected ? (
            <Button
              variant="primary"
              icon={RefreshCw}
              onClick={() => navigate('/collections/integrations/quickbooks/sync')}
            >
              Sync Now
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={Link2}
              onClick={() => initiate.mutate()}
              loading={initiate.isPending}
            >
              Connect to QuickBooks
            </Button>
          )
        }
      />

      <Card title="QuickBooks Connection">
        <div className={styles.statusRow}>
          {data.connected ? (
            <Badge variant="success" icon={<CheckCircle size={12} aria-hidden />}>
              Connected
            </Badge>
          ) : (
            <Badge variant="danger" icon={<XCircle size={12} aria-hidden />}>
              Not Connected
            </Badge>
          )}
        </div>

        {data.connected ? (
          <>
            <dl className={styles.dl}>
              <div>
                <dt>Realm ID</dt>
                <dd>{data.realm_id ?? '—'}</dd>
              </div>
              <div>
                <dt>Connected</dt>
                <dd>
                  {formatDate(data.connected_at)}{' '}
                  {data.connected_by ? `by ${data.connected_by}` : ''}
                </dd>
              </div>
              <div>
                <dt>Token expires</dt>
                <dd>{formatDate(data.token_expires_at)}</dd>
              </div>
              <div>
                <dt>Last sync</dt>
                <dd>
                  {formatDate(data.last_sync_at)}{' '}
                  {data.last_sync_status ? `(${data.last_sync_status})` : ''}
                </dd>
              </div>
            </dl>

            <div className={styles.connectedActions}>
              <Button
                variant="danger"
                icon={Unlink}
                onClick={() => setDisconnectOpen(true)}
                loading={disconnect.isPending}
              >
                Disconnect
              </Button>
            </div>

            <div className={styles.navActions}>
              <Button
                variant="secondary"
                icon={Settings}
                onClick={() => navigate('/collections/integrations/quickbooks/settings')}
              >
                Settings
              </Button>
              <Button
                variant="secondary"
                icon={RefreshCw}
                onClick={() => navigate('/collections/integrations/quickbooks/sync')}
              >
                Sync
              </Button>
              <Button
                variant="secondary"
                icon={Users}
                onClick={() => navigate('/collections/integrations/quickbooks/customers')}
              >
                Customer Mapping
              </Button>
              <Button
                variant="secondary"
                icon={Building2}
                onClick={() => navigate('/collections/integrations/quickbooks/vendors')}
              >
                Vendor Mapping
              </Button>
            </div>
          </>
        ) : (
          <p className={styles.muted}>
            Connect your QuickBooks account to automatically sync paid transactions as expenses
            with attached receipts.
          </p>
        )}
      </Card>

      <ConfirmModal
        open={disconnectOpen}
        title="Disconnect QuickBooks?"
        message="Disconnecting clears the stored OAuth tokens for this company. You'll need to reauthorise to sync again."
        confirmLabel="Disconnect"
        cancelLabel="Cancel"
        variant="danger"
        loading={disconnect.isPending}
        onCancel={() => setDisconnectOpen(false)}
        onConfirm={() =>
          disconnect.mutate(undefined, {
            onSuccess: () => setDisconnectOpen(false),
          })
        }
      />
    </div>
  );
}
